import { Fragment } from 'react';
import { z } from 'zod';
import { Pill } from '#components/atoms/pill/Pill.tsx';
import {
  LOGGING_FORMAT_LABELS,
  LOGGING_TIMESTAMP_FORMAT_LABELS,
  STATIC_MAP_NETWORK_LABELS,
  STATS_TYPE_LABELS,
} from '#components/patterns/config-overrides/labels/ConfigOverrideLabels.ts';
import { TagLinkWithParens } from '#components/patterns/entity-links/tag-link/TagLinkWithParens.tsx';
import type { ConfigOverride, TagName } from '#dn-types/host.ts';
import { UnreachableCodeError } from '#util/assert-unreachable.ts';

type Props = {
  configOverride: ConfigOverride;
  source?: TagName | 'host';
  /** Controls whether to show an alert pill in remote and local allow lists. */
  usePills?: boolean;
};

/**
 * {@link ConfigOverrideValue} takes a configOverride and renders the value as a human-readable string.
 *
 * If a source is provided, it will be shown in parens as a link to the tag.
 */
export function ConfigOverrideValue({ configOverride, usePills, source }: Props) {
  const tagSource = source && source !== 'host' ? <TagLinkWithParens tagName={source} /> : null;

  return (
    <span>
      {formatConfigValue(configOverride, usePills)} {tagSource}
    </span>
  );
}

function formatConfigValue(configOverride: ConfigOverride, usePills?: boolean) {
  const { key, value } = configOverride;

  if (key === 'logging.format') {
    return LOGGING_FORMAT_LABELS[value];
  }
  if (key === 'logging.timestamp_format') {
    return LOGGING_TIMESTAMP_FORMAT_LABELS[value];
  }
  if (key === 'logging.level') {
    return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
  }
  if (key === 'stats.type') {
    return STATS_TYPE_LABELS[value];
  }
  if (key === 'static_map.network') {
    return STATIC_MAP_NETWORK_LABELS[value];
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return value.toString();
  }
  if (typeof value === 'boolean') {
    if (key === 'tun.disabled' || key === 'listen.udp_offloads' || key === 'tun.use_offloads') {
      return value ? 'True' : 'False';
    }
    return value ? 'On' : 'Off';
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (key === 'lighthouse.local_allow_list' || key === 'lighthouse.remote_allow_list') {
    const { interfaces, ...rest } = value;

    const allRules = Object.entries(z.record(z.boolean()).parse({ ...interfaces, ...rest }));

    if (usePills) {
      return allRules.map(([iface, allow], index) => (
        <Fragment key={iface}>
          {iface}
          {!allow && (
            <>
              {' '}
              <Pill variant="alert">Donʼt Allow</Pill>
            </>
          )}
          {index < allRules.length - 1 ? ', ' : ''}
        </Fragment>
      ));
    }

    const allowedRules = allRules.filter(([, allow]) => allow);
    const notAllowedRules = allRules.filter(([, allow]) => !allow);
    return (
      <>
        {allowedRules.length ?
          <>
            Allow: {allowedRules.map(([iface]) => iface).join(', ')}
            <br />
          </>
        : null}
        {notAllowedRules.length ?
          <>Donʼt allow: {notAllowedRules.map(([iface]) => iface).join(', ')}</>
        : null}
      </>
    );
  }

  // We handle all the types above, "value" is now typed as "never"
  throw new UnreachableCodeError(value);
}
